import clsx from "clsx";
import { Controller, useFormContext } from "react-hook-form";

import { IDropdownOptionV2 } from "@dl/declarations";
import { INPUT_SIZES, POST_SET_VALUE_OPTIONS } from "../../utils/constants";

interface Props {
    name: string;
    options: Record<string, IDropdownOptionV2[]>;
    inputSize?: keyof typeof INPUT_SIZES;
    distribution?: {
        type: 'row' | 'grid'
    }

    showRadio?: boolean;
    allowMultiSelect?: boolean;
    maxSelectionForMultiSelect?: number;
    actionIcon?: React.ReactNode;
    actionHandler?: (option: IDropdownOptionV2) => void;

    disabled?: boolean;
    valueKey?: keyof Pick<IDropdownOptionV2, "_id" | "slug">;
    labelKey?: keyof Pick<IDropdownOptionV2, "label" | "slug">;
    renderLabel?: (option: IDropdownOptionV2) => React.ReactNode;
}

export const ControlledRadio: React.FC<Props> = (props) => {
    const {
        name,
        options,
        showRadio = false,
        inputSize = "full",
        allowMultiSelect = false,
        maxSelectionForMultiSelect = 0,
        actionIcon,
        actionHandler,
        valueKey = "slug",
        labelKey = "label",
        renderLabel,
        disabled = false,
        distribution = {
            type: 'row'
        }
    } = props;

    const { formState, control, setValue, watch } = useFormContext();

    const selected = watch(name);

    const toggleItem = (option: IDropdownOptionV2) => {
        if (allowMultiSelect) {
            const current = new Set(selected);

            if (current.has(option[valueKey])) {
                current.delete(option[valueKey]);
            } else {
                current.add(option[valueKey]);
            }

            if (maxSelectionForMultiSelect > 0 && current.size > maxSelectionForMultiSelect) {
                const values = Array.from(current);
                current.delete(values[0]);
            }

            setValue(name, Array.from(current), POST_SET_VALUE_OPTIONS);
        }
        else {
            setValue(name, option[valueKey], POST_SET_VALUE_OPTIONS);
        }
    };

    const isSelected = (item: string) => {
        if (allowMultiSelect) {
            return (selected || []).includes(item)
        }

        return selected === item;
    };

    const hasError = Boolean(formState?.errors?.[name]);

    return (
        <Controller
            name={name}
            control={control}
            disabled={disabled}
            render={() => (
                <div
                    className={clsx(
                        INPUT_SIZES[inputSize],
                        "min-h-24 max-h-36 overflow-y-auto border rounded ps-2 pe-4 gap-y-2",
                        disabled && "opacity-50 cursor-not-allowed pointer-events-none",
                        hasError ? "border-red-500" : "border-cs-300"
                    )}
                >
                    <div
                        className={clsx(
                            "flex flex-col my-1",
                        )}
                    >
                        {Object.entries(options).map(([key, group], _, optionsArray) => {
                            return (
                                <div key={key}>
                                    {optionsArray.length > 1 && (
                                        <h4 className="first-letter:uppercase font-semibold">
                                            {key}
                                        </h4>
                                    )}

                                    <div
                                        className={clsx(
                                            distribution.type === "row" && "flex flex-col",
                                            distribution.type === "grid" && "grid grid-cols-2 2xl:grid-cols-3",
                                            "gap-1",
                                        )}
                                    >
                                        {group.map((option) => {
                                            const isOptionSelected = isSelected(option[valueKey])
                                            return (
                                                <button
                                                    type="button"
                                                    key={option[valueKey]}
                                                    onClick={() => toggleItem(option)}
                                                    className={clsx(
                                                        `flex items-center justify-start gap-2 w-full py-1 rounded bg-white truncate ...`,
                                                        isOptionSelected
                                                            ? 'border-2 border-[#237BFF] text-[#237BFF] font-semibold'
                                                            : 'border hover:bg-gray-100',
                                                        distribution.type === "grid" && 'px-1',
                                                        distribution.type === "row" && 'px-3',
                                                    )}
                                                >
                                                    {showRadio && (
                                                        <input
                                                            readOnly
                                                            type="radio"
                                                            checked={isOptionSelected}
                                                            className="accent-[#237BFF]"
                                                        />
                                                    )}

                                                    <span>
                                                        {renderLabel ? renderLabel(option) : option[labelKey]}
                                                    </span>

                                                    {(actionIcon && actionHandler) && (
                                                        <button className="ms-auto" onClick={() => actionHandler(option)}>
                                                            {actionIcon}
                                                        </button>
                                                    )}
                                                </button>
                                            )
                                        })}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}
        />
    );
}